import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getStatus } from "../services/api";
import axios from "axios";
import { RootState } from "./store";

// Define proper types for system status
export interface SystemStatus {
  apiStatus: "operational" | "degraded" | "down";
  serverHealth: "good" | "warning" | "critical";
  lastUpdated: string;
  services: {
    authentication: boolean;
    database: boolean;
    weather: boolean;
    satellites: boolean;
    notifications: boolean;
  };
  performanceMetrics?: {
    responseTime: number;
    serverLoad: number;
    memoryUsage: number;
  };
  maintenance?: {
    scheduled: boolean;
    startTime?: string;
    endTime?: string;
    message?: string;
  };
  version: string;
}

// Types for notifications
export interface SystemNotification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  message: string;
  title?: string;
  duration?: number;
  createdAt: string;
  read?: boolean;
  action?: {
    label: string;
    callback: string; // Name of action to dispatch
  };
}

interface SystemState {
  status: SystemStatus | null;
  loading: boolean;
  error: string | null;
  notifications: SystemNotification[];
  unreadNotificationCount: number;
  maintenanceMode: boolean;
  lastChecked: string | null;
}

const initialState: SystemState = {
  status: null,
  loading: false,
  error: null,
  notifications: [],
  unreadNotificationCount: 0,
  maintenanceMode: false,
  lastChecked: null,
};

// Create async thunk for system status
export const fetchSystemStatus = createAsyncThunk(
  "system/fetchStatus",
  async (_, { rejectWithValue }) => {
    try {
      const data = await getStatus();
      return data as SystemStatus;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return rejectWithValue(
          error.response.data.message || "Failed to fetch system status",
        );
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("Unknown error occurred");
    }
  },
);

const systemSlice = createSlice({
  name: "system",
  initialState,
  reducers: {
    setError(
      state,
      action: PayloadAction<{
        message: string;
        source: string;
        timestamp: string;
      }>,
    ) {
      const errorId = `error-${Date.now()}`;

      // Add error to notifications
      state.notifications.push({
        id: errorId,
        type: "error",
        message: action.payload.message,
        title: `Error in ${action.payload.source}`,
        createdAt: action.payload.timestamp,
        duration: 5000,
        read: false,
      });

      // Update unread count
      state.unreadNotificationCount += 1;
    },

    setNotification(
      state,
      action: PayloadAction<{
        type: "info" | "success" | "warning" | "error";
        message: string;
        title?: string;
        duration?: number;
        action?: {
          label: string;
          callback: string;
        };
      }>,
    ) {
      const notificationId = `notification-${Date.now()}`;

      // Add notification
      state.notifications.push({
        id: notificationId,
        type: action.payload.type,
        message: action.payload.message,
        title: action.payload.title,
        duration: action.payload.duration || 3000,
        createdAt: new Date().toISOString(),
        read: false,
        action: action.payload.action,
      });

      // Update unread count
      state.unreadNotificationCount += 1;
    },

    markNotificationAsRead(state, action: PayloadAction<string>) {
      const notification = state.notifications.find(
        (n) => n.id === action.payload,
      );

      if (notification && !notification.read) {
        notification.read = true;
        state.unreadNotificationCount = Math.max(
          0,
          state.unreadNotificationCount - 1,
        );
      }
    },

    markAllNotificationsAsRead(state) {
      state.notifications.forEach((notification) => {
        notification.read = true;
      });
      state.unreadNotificationCount = 0;
    },

    dismissNotification(state, action: PayloadAction<string>) {
      const notificationIndex = state.notifications.findIndex(
        (n) => n.id === action.payload,
      );

      if (notificationIndex !== -1) {
        const notification = state.notifications[notificationIndex];
        if (!notification.read) {
          state.unreadNotificationCount = Math.max(
            0,
            state.unreadNotificationCount - 1,
          );
        }
        state.notifications.splice(notificationIndex, 1);
      }
    },

    clearAllNotifications(state) {
      state.notifications = [];
      state.unreadNotificationCount = 0;
    },

    setMaintenanceMode(state, action: PayloadAction<boolean>) {
      state.maintenanceMode = action.payload;

      // If entering maintenance mode, add a notification
      if (action.payload) {
        state.notifications.push({
          id: `maintenance-${Date.now()}`,
          type: "warning",
          title: "Scheduled Maintenance",
          message:
            "The system is currently undergoing scheduled maintenance. Some features may be temporarily unavailable.",
          createdAt: new Date().toISOString(),
          read: false,
        });
        state.unreadNotificationCount += 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSystemStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload;
        state.lastChecked = new Date().toISOString();

        // Check for maintenance status
        if (action.payload.maintenance?.scheduled) {
          state.maintenanceMode = true;
        } else {
          state.maintenanceMode = false;
        }

        // If any services are down, create a notification
        const downServices = Object.entries(action.payload.services)
          .filter(([_, isUp]) => !isUp)
          .map(([service]) => service);

        if (downServices.length > 0) {
          state.notifications.push({
            id: `service-down-${Date.now()}`,
            type: "warning",
            title: "Service Disruption",
            message: `The following services are currently unavailable: ${downServices.join(", ")}`,
            createdAt: new Date().toISOString(),
            read: false,
          });
          state.unreadNotificationCount += 1;
        }
      })
      .addCase(fetchSystemStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;

        // Add an error notification
        state.notifications.push({
          id: `status-error-${Date.now()}`,
          type: "error",
          title: "System Status Error",
          message: `Could not fetch system status: ${action.payload}`,
          createdAt: new Date().toISOString(),
          read: false,
        });
        state.unreadNotificationCount += 1;
      });
  },
});

export const {
  setError,
  setNotification,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  dismissNotification,
  clearAllNotifications,
  setMaintenanceMode,
} = systemSlice.actions;

// Selectors
export const selectSystemStatus = (state: RootState) => state.system.status;
export const selectSystemLoading = (state: RootState) => state.system.loading;
export const selectSystemError = (state: RootState) => state.system.error;
export const selectNotifications = (state: RootState) =>
  state.system.notifications;
export const selectUnreadNotificationCount = (state: RootState) =>
  state.system.unreadNotificationCount;
export const selectIsMaintenanceMode = (state: RootState) =>
  state.system.maintenanceMode;
export const selectIsApiOperational = (state: RootState) =>
  state.system.status?.apiStatus === "operational";

export default systemSlice.reducer;
